import React, { useMemo } from "react";

import { faCheck, faPlus } from "@fortawesome/pro-regular-svg-icons";
import { twJoin } from "tailwind-merge";

import { CustomPopover } from "src/base-components/Popover";
import { useOptionListEditor } from "src/cases/caseView/blocks/cellEditors/useOptionListEditor";
import { Icon } from "src/design-system/Icon";
import { Pill } from "src/design-system/Pill";

export const EnumEditor: React.FC<{
  value: unknown;
  columnValues?: unknown[];
  onChange: (newValue: unknown) => void;
  onBlur: () => void;
  hasLeftIcon?: boolean;
}> = ({ value, columnValues, onChange, onBlur, hasLeftIcon }) => {
  const options = useMemo(
    () =>
      Array.from(
        new Set([
          ...(columnValues ?? [])
            .filter((v) => v !== null && v !== undefined)
            .map((v) => String(v)),
          ...(value !== null && value !== undefined ? [String(value)] : []),
        ]),
      ),
    [columnValues, value],
  );

  const {
    isAddingValue,
    setIsAddingValue,
    search,
    setSearch,
    filteredOptions,
    focusedIndex,
    setFocusedIndex,
    listRef,
    triggerRef,
    moveFocus,
    exitAddMode,
  } = useOptionListEditor({
    options,
    onBlur,
    initialIndex: Math.max(
      0,
      options.findIndex((o) => String(value) === o),
    ),
  });

  const handleSelectOption = (opt: string) => {
    const isCurrentlySelected =
      value !== null && value !== undefined && String(value) === opt;
    onChange(isCurrentlySelected ? null : opt);
    onBlur();
  };

  const handleListKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case "ArrowDown":
      case "ArrowUp":
        e.preventDefault();
        moveFocus(e.key === "ArrowDown" ? "down" : "up");
        break;
      case "Enter":
        e.preventDefault();
        if (focusedIndex < filteredOptions.length) {
          handleSelectOption(filteredOptions[focusedIndex]);
        } else {
          setIsAddingValue(true);
        }
        break;
      case "Escape":
        e.preventDefault();
        onBlur();
        break;
      case "Tab":
        return;
    }
    e.stopPropagation();
  };

  const handleInputKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case "Escape":
        e.preventDefault();
        exitAddMode();
        break;
      case "Enter":
        e.preventDefault();
        if (focusedIndex < filteredOptions.length) {
          handleSelectOption(filteredOptions[focusedIndex]);
        } else if (search) {
          handleSelectOption(search);
        } else if (filteredOptions.length > 0) {
          handleSelectOption(filteredOptions[0]);
        }
        break;
      case "ArrowDown":
      case "ArrowUp":
        e.preventDefault();
        moveFocus(e.key === "ArrowDown" ? "down" : "up");
        break;
      case "Tab":
        return;
    }
    e.stopPropagation();
  };

  return (
    <div ref={triggerRef} className="h-full w-full min-w-0">
      <CustomPopover
        button={
          isAddingValue ? (
            <div
              className={twJoin(
                "flex h-full w-full items-center py-1.5 pr-2",
                hasLeftIcon ? "pl-6" : "pl-1.5",
              )}
            >
              <input
                ref={(node) => node?.focus()}
                aria-autocomplete="list"
                aria-expanded={true}
                className="h-full w-full bg-transparent font-inter-normal-12px outline-none"
                placeholder="Type new value..."
                role="combobox"
                value={search}
                onBlur={onBlur}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={handleInputKeyDown}
              />
            </div>
          ) : (
            <div
              className={twJoin(
                "flex h-full w-full items-center py-1.5 pr-2",
                hasLeftIcon ? "pl-6" : "pl-1.5",
              )}
            >
              {value !== null && value !== undefined && (
                <Pill size="sm" variant="gray">
                  <Pill.Text>{String(value)}</Pill.Text>
                </Pill>
              )}
            </div>
          )
        }
        className="decideScrollbar max-h-48 w-48 overflow-auto"
        isOpen={true}
        offsetX={8}
        placement="bottom-start"
        wrapperClassName="w-full"
      >
        <div
          ref={listRef}
          className="outline-none"
          role="listbox"
          tabIndex={isAddingValue ? undefined : 0}
          onKeyDown={!isAddingValue ? handleListKeyDown : undefined}
        >
          {filteredOptions.map((opt, index) => {
            const isSelected = String(value) === opt;
            return (
              <button
                key={opt}
                aria-selected={isSelected}
                className={twJoin(
                  "flex w-full items-center justify-between px-4 py-2.5 text-left",
                  index === focusedIndex ? "bg-gray-50" : "hover:bg-gray-50",
                )}
                role="option"
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleSelectOption(opt);
                }}
                onMouseEnter={() => setFocusedIndex(index)}
              >
                <Pill size="sm" variant="gray">
                  <Pill.Text>{opt}</Pill.Text>
                </Pill>
                {isSelected && (
                  <Icon icon={faCheck} size="xs" variant="active" />
                )}
              </button>
            );
          })}
          {filteredOptions.length === 0 && !search && (
            <div className="px-4 py-2.5 font-inter-normal-13px text-gray-400">
              No options
            </div>
          )}
          {filteredOptions.length > 0 && (
            <div className="my-1 border-gray-100 border-t" />
          )}
          {isAddingValue && search ? (
            <button
              className={twJoin(
                "flex w-full items-center gap-1.5 px-4 py-2.5 text-left font-inter-normal-13px text-gray-800",
                focusedIndex === filteredOptions.length
                  ? "bg-gray-50"
                  : "hover:bg-gray-50",
              )}
              role="option"
              onMouseDown={(e) => {
                e.preventDefault();
                handleSelectOption(search);
              }}
            >
              <Icon icon={faPlus} size="2xs" />
              <span>Add</span>
              <Pill size="sm" variant="gray">
                <Pill.Text>{search}</Pill.Text>
              </Pill>
            </button>
          ) : (
            <button
              className={twJoin(
                "flex w-full items-center gap-1.5 px-4 py-2.5 text-left font-inter-normal-13px text-gray-800",
                !isAddingValue && focusedIndex === filteredOptions.length
                  ? "bg-gray-50"
                  : "hover:bg-gray-50",
              )}
              role="option"
              onMouseDown={(e) => {
                e.preventDefault();
                setIsAddingValue(true);
              }}
              onMouseEnter={() => setFocusedIndex(filteredOptions.length)}
            >
              <Icon icon={faPlus} size="2xs" />
              <span>Add value</span>
            </button>
          )}
        </div>
      </CustomPopover>
    </div>
  );
};
